import React, { useEffect, useState } from "react";
import { Smile, Wind, Moon, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { api } from "../../lib/api";

/**
 * "This week" mini-summary on Wellbeing.
 *
 * Shows rolling 7-day averages for mood, stress, and sleep, plus a tiny SVG
 * sparkline and a delta arrow vs. the previous 7 days. Turns check-ins from
 * log-and-forget into a felt sense of trend.
 */
const Sparkline = ({ values, colour, ariaLabel }) => {
  if (!values || values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const W = 60;
  const H = 18;
  const step = W / (values.length - 1);
  const points = values.map((v, i) => `${(i * step).toFixed(1)},${(H - ((v - min) / range) * H).toFixed(1)}`).join(" ");
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="shrink-0" aria-label={ariaLabel}>
      <polyline points={points} fill="none" stroke={colour} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={W} cy={H - ((values[values.length - 1] - min) / range) * H} r="2" fill={colour} />
    </svg>
  );
};

const Delta = ({ delta, invert = false, unit = "" }) => {
  // invert: for stress, LOWER is better — flip the arrow colour meaning
  const better = invert ? delta < 0 : delta > 0;
  const worse = invert ? delta > 0 : delta < 0;
  const abs = Math.abs(delta).toFixed(1);
  if (Math.abs(delta) < 0.1) {
    return <span className="inline-flex items-center gap-0.5 text-[10px] font-black text-zinc-500"><Minus className="w-2.5 h-2.5" /> flat</span>;
  }
  const Icon = delta > 0 ? TrendingUp : TrendingDown;
  const colour = better ? "text-[#BEFF38]" : worse ? "text-amber-400" : "text-zinc-500";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-black tabular-nums ${colour}`}>
      <Icon className="w-2.5 h-2.5" />
      {abs}{unit}
    </span>
  );
};

const Metric = ({ icon: Icon, label, avg, unit, values, delta, invert, colour, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-zinc-800 bg-[#0b0c10] p-3">
    <div className="flex items-center gap-1.5">
      <Icon className="w-3 h-3 text-zinc-500" strokeWidth={2.5} />
      <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">{label}</p>
    </div>
    <div className="mt-1.5 flex items-end gap-2 justify-between">
      <p className="text-white text-2xl font-black tabular-nums leading-none">
        {avg === null ? <span className="text-zinc-600">—</span> : `${avg.toFixed(1)}${unit}`}
      </p>
      <Sparkline values={values} colour={colour} ariaLabel={`${label} last 7 days`} />
    </div>
    <div className="mt-1.5">
      {delta === null
        ? <span className="text-[10px] font-black text-zinc-600">Not enough data yet</span>
        : <span className="text-[10px] text-zinc-500">vs last week: <Delta delta={delta} invert={invert} unit={unit} /></span>}
    </div>
  </div>
);

export default function WellbeingWeekStrip() {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({ mood: null, stress: null, sleep: null });

  useEffect(() => {
    (async () => {
      try {
        const checkins = await api.wellbeingCheckins(14).catch(() => []);
        setState(compute(checkins || []));
      } catch { /* silent — enrichment only */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || (state.mood.avg === null && state.stress.avg === null && state.sleep.avg === null)) {
    return null;
  }

  return (
    <div
      data-testid="wellbeing-week-strip"
      className="rounded-2xl border border-white/5 bg-zinc-950/40 p-4"
    >
      <p className="text-[10px] uppercase tracking-[0.2em] font-black text-zinc-500">This week</p>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <Metric testid="wellbeing-week-mood"
                icon={Smile} label="Mood" unit="/10"
                avg={state.mood.avg} values={state.mood.values} delta={state.mood.delta}
                invert={false} colour="#BEFF38" />
        <Metric testid="wellbeing-week-stress"
                icon={Wind} label="Stress" unit="/10"
                avg={state.stress.avg} values={state.stress.values} delta={state.stress.delta}
                invert={true} colour="#ff7a1a" />
        <Metric testid="wellbeing-week-sleep"
                icon={Moon} label="Sleep" unit="h"
                avg={state.sleep.avg} values={state.sleep.values} delta={state.sleep.delta}
                invert={false} colour="#93c5fd" />
      </div>
    </div>
  );
}

// -------- helpers --------

function compute(checkins) {
  // checkins are date-descending; slice into two 7-day windows.
  const sorted = [...checkins].sort((a, b) => (a.date < b.date ? 1 : -1)); // newest first
  const thisWeek = sorted.slice(0, 7);
  const lastWeek = sorted.slice(7, 14);

  const avgOf = (list, key) => {
    const vals = list.map((c) => Number(c?.[key])).filter((v) => Number.isFinite(v) && v > 0);
    return vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
  };
  const valuesOf = (list, key) =>
    [...list].reverse() // oldest→newest for the sparkline
      .map((c) => Number(c?.[key]))
      .filter((v) => Number.isFinite(v) && v > 0);

  const build = (key) => {
    const avg = avgOf(thisWeek, key);
    const prev = avgOf(lastWeek, key);
    return {
      avg,
      values: valuesOf(thisWeek, key),
      delta: (avg !== null && prev !== null) ? avg - prev : null,
    };
  };

  return {
    mood: build("mood"),
    stress: build("stress"),
    sleep: build("sleep_hours"),
  };
}
